import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Crown02Icon } from "@hugeicons/core-free-icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { createCreditsApi } from "@/lib/api/credits";
import { CREDITS_BALANCE_QUERY_KEY } from "@/lib/hooks/use-credits";

const PLAN_LABELS: Record<string, string> = { pro: "Pro", max: "Max" };
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 15000;

interface CheckoutSuccessModalProps {
  onSuccess?: () => void;
}

export function CheckoutSuccessModal({ onSuccess }: CheckoutSuccessModalProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const isOpen = searchParams.get("checkout") === "success";

  const [planName, setPlanName] = useState<string | null>(null);
  const [pollDone, setPollDone] = useState(false);
  const confettiFired = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      return;
    }

    const deadline = Date.now() + POLL_MAX_MS;
    const api = createCreditsApi({ getToken });

    async function poll() {
      try {
        const balance = await api.getBalance();
        queryClient.setQueryData(CREDITS_BALANCE_QUERY_KEY, balance);
        if (balance.plan !== "free") {
          setPlanName(PLAN_LABELS[balance.plan] ?? balance.plan);
          setPollDone(true);
          return;
        }
      } catch {
        /* ignore poll errors */
      }
      if (Date.now() >= deadline) {
        setPollDone(true);
        return;
      }
      pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();

    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pollDone && planName && !confettiFired.current) {
      confettiFired.current = true;
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#8b5cf6", "#c4b5fd", "#ffffff"],
      });
    }
  }, [pollDone, planName]);

  function handleClose() {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    // Reset state for potential re-open
    setPlanName(null);
    setPollDone(false);
    confettiFired.current = false;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("checkout");
      next.delete("type");
      return next;
    });
    queryClient.invalidateQueries({ queryKey: CREDITS_BALANCE_QUERY_KEY });
    onSuccess?.();
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && pollDone) handleClose();
      }}
    >
      <DialogContent
        className="sm:max-w-sm"
        onInteractOutside={(e) => {
          if (!pollDone) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!pollDone) e.preventDefault();
        }}
      >
        <DialogHeader className="items-center gap-3 pb-2">
          <div className="flex size-14 items-center justify-center rounded-full bg-brand/10">
            <HugeiconsIcon icon={Crown02Icon} className="size-7 text-brand" />
          </div>
          <DialogTitle className="text-center text-xl">
            {pollDone && planName
              ? `Welcome to ${planName}!`
              : "Processing your upgrade…"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {pollDone && planName
              ? "Your plan is active and credits are ready to use."
              : pollDone
                ? "Your upgrade is being processed. Credits will appear shortly."
                : "Confirming your subscription with Stripe…"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 pt-2">
          {pollDone && planName ? (
            <Badge
              variant="secondary"
              className="px-4 py-1.5 text-sm font-semibold"
            >
              {planName} Plan Active
            </Badge>
          ) : (
            <div className="h-1 w-32 overflow-hidden rounded-full bg-muted">
              <div className="bg-brand h-full animate-pulse rounded-full" />
            </div>
          )}

          <Button
            className="w-full"
            onClick={handleClose}
            disabled={!pollDone}
          >
            {pollDone ? "Get started!" : "Please wait…"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
